import React, { useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { PlusCircle, Layers, Landmark, CheckCircle2, X } from 'lucide-react';
import EncryptedBadge from '../security/EncryptedBadge';

const AddInvestmentsTab = ({
  isReadOnly, handleReadOnlyAction, onAddManually, onAddBatch, onUpload,
  importSuccessMessage, onDismissImportSuccess,
}) => {
  const handleClick = (action) => isReadOnly ? handleReadOnlyAction() : action();

  // Auto-dismiss the success banner after 8s so the tab returns to its clean state.
  useEffect(() => {
    if (!importSuccessMessage) return undefined;
    const t = setTimeout(() => onDismissImportSuccess?.(), 8000);
    return () => clearTimeout(t);
  }, [importSuccessMessage, onDismissImportSuccess]);

  return (
    <div>
      <h2 className="text-lg sm:text-2xl font-serif font-bold text-slate-900 mb-2">Add Investments</h2>
      <p className="text-sm text-slate-500 mb-4">Choose how you&apos;d like to add investments to your portfolio</p>
      <div className="max-w-3xl mb-6">
        <EncryptedBadge variant="banner" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
        <Card
          data-testid="add-manually-card"
          className="cursor-pointer hover:shadow-lg hover:border-emerald-300 transition-all"
          onClick={() => handleClick(onAddManually)}
        >
          <CardContent className="p-6 flex flex-col items-center text-center space-y-3">
            <div className="p-4 bg-emerald-100 rounded-full">
              <PlusCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Add a single Investment</h3>
            <p className="text-sm text-slate-500">Add one investment with full details using our guided form</p>
          </CardContent>
        </Card>
        <Card
          data-testid="add-batch-card"
          className="cursor-pointer hover:shadow-lg hover:border-amber-300 transition-all"
          onClick={() => handleClick(onAddBatch)}
        >
          <CardContent className="p-6 flex flex-col items-center text-center space-y-3">
            <div className="p-4 bg-amber-100 rounded-full">
              <Layers className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Add a Batch of Investments</h3>
            <p className="text-sm text-slate-500">Pick a type once and add up to 10 entries together</p>
          </CardContent>
        </Card>
        <Card
          data-testid="institution-import-card"
          className="cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all"
          onClick={() => handleClick(onUpload)}
        >
          <CardContent className="p-6 flex flex-col items-center text-center space-y-3">
            <div className="p-4 bg-blue-100 rounded-full">
              <Landmark className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Import from your institution</h3>
            <p className="text-sm text-slate-500">Upload your bank or asset manager&apos;s own file &mdash; no reformatting needed</p>
          </CardContent>
        </Card>
      </div>
      {importSuccessMessage && (
        <div
          className="mt-6 max-w-3xl flex items-start gap-3 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 sm:px-5 sm:py-4 shadow-sm"
          role="status"
          aria-live="polite"
          data-testid="institution-import-success-banner"
        >
          <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
          <p className="flex-1 text-sm sm:text-base text-emerald-900 leading-relaxed">
            {importSuccessMessage}
          </p>
          <button
            type="button"
            onClick={() => onDismissImportSuccess?.()}
            className="p-1 rounded hover:bg-emerald-100 text-emerald-700"
            aria-label="Dismiss"
            data-testid="institution-import-success-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AddInvestmentsTab;
